import React, { useState, useEffect, useCallback } from 'react';
import { TriangleAlert } from 'lucide-react';
import { GetBillingSummary } from '../../Client/ManagementServiceClient';
import { getAuth } from 'firebase/auth';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
    eachLabel,
    itemLabel,
    shareLabel,
    toCategoryGroups,
    toProjectGroups,
} from './labels';
import './styles.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const RANGE_PRESETS = {
    TODAY: 'today',
    WEEK: 'this_week',
    MONTH: 'this_month',
    YEAR: 'this_year',
    CUSTOM: 'custom'
};

function Billing() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [error, setError] = useState('');
    const [selectedRange, setSelectedRange] = useState(RANGE_PRESETS.MONTH);
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    // "What am I spending on" and "what did that video cost" are different
    // questions off the same rows, so this only changes how they are grouped.
    const [groupBy, setGroupBy] = useState('category');
    const [openGroups, setOpenGroups] = useState({});

    const toYYYYMMDD = useCallback((dateObj) => {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
    }, []);

    const getRangeBounds = useCallback(() => {
        const now = new Date();

        if (selectedRange === RANGE_PRESETS.CUSTOM) {
            const startDate = customStartDate ? customStartDate.replaceAll('-', '') : null;
            const endDate = customEndDate ? customEndDate.replaceAll('-', '') : null;
            return { startDate, endDate };
        }

        if (selectedRange === RANGE_PRESETS.TODAY) {
            const today = toYYYYMMDD(now);
            return { startDate: today, endDate: today };
        }

        if (selectedRange === RANGE_PRESETS.WEEK) {
            const current = new Date(now);
            const dayOfWeek = current.getDay();
            const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            const weekStart = new Date(current);
            weekStart.setDate(current.getDate() + mondayOffset);
            return { startDate: toYYYYMMDD(weekStart), endDate: toYYYYMMDD(now) };
        }

        if (selectedRange === RANGE_PRESETS.MONTH) {
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            return { startDate: toYYYYMMDD(monthStart), endDate: toYYYYMMDD(now) };
        }

        const yearStart = new Date(now.getFullYear(), 0, 1);
        return { startDate: toYYYYMMDD(yearStart), endDate: toYYYYMMDD(now) };
    }, [selectedRange, customStartDate, customEndDate, toYYYYMMDD]);

    const loadData = useCallback(async (startDate = null, endDate = null) => {
        try {
            setLoading(true);
            setError('');
            
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();

            const summaryResult = await GetBillingSummary(idToken, startDate, endDate);

            if (summaryResult.status === 200) {
                setSummary(summaryResult.myJson);
            } else if (summaryResult.status === 401) {
                setError('Unauthorized. Please log in again.');
                return;
            } else {
                setError(summaryResult.myJson?.detail || 'Failed to load billing summary.');
            }
        } catch (err) {
            console.error('Error loading data:', err);
            setError('An error occurred while loading data.');
        } finally {
            setLoading(false);
            setHasLoadedOnce(true);
        }
    }, []);

    useEffect(() => {
        const { startDate, endDate } = getRangeBounds();
        loadData(startDate, endDate);
    }, [getRangeBounds, loadData]);

    const formatPrice = (price, currency) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(price);
    };

    const formatDateKey = (dateKey) => {
        const key = String(dateKey || '');
        if (key.length !== 8) return key;
        const year = key.slice(0, 4);
        const month = key.slice(4, 6);
        const day = key.slice(6, 8);
        return `${year}-${month}-${day}`;
    };

    const buildDailyChartData = (dailyFlowCost) => {
        if (!dailyFlowCost || dailyFlowCost.length === 0) {
            return {
                labels: [],
                datasets: []
            };
        }

        const sorted = [...dailyFlowCost].sort((a, b) => Number(a.datekey) - Number(b.datekey));
        const labels = [...new Set(sorted.map(item => formatDateKey(item.datekey)))];
        const flows = [...new Set(sorted.map(item => item.entity_flow || 'unknown'))];

        // The chart draws on a canvas, which cannot resolve var(); read the
        // design tokens' values so the series use the app's own colours.
        const token = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
        const colorPalette = ['--sb-blue', '--sb-good', '--sb-caution', '--sb-marker', '--sb-ink-2', '--sb-line-control']
            .map(token)
            .filter(Boolean);

        const datasets = flows.map((flow, flowIndex) => {
            const color = colorPalette[flowIndex % colorPalette.length];
            const mapByDate = new Map();

            sorted
                .filter(item => (item.entity_flow || 'unknown') === flow)
                .forEach(item => {
                    mapByDate.set(formatDateKey(item.datekey), Number(item.cost_usd || 0));
                });

            return {
                label: flow,
                data: labels.map(label => mapByDate.get(label) || 0),
                borderColor: color,
                backgroundColor: color,
                tension: 0.25,
                pointRadius: 3,
                pointHoverRadius: 5
            };
        });

        return { labels, datasets };
    };

    if (loading && !hasLoadedOnce) {
        return (
            <div className="billing-container">
                <div className="billing-loading-skeleton" aria-live="polite" aria-busy="true">
                    <p className="billing-loading-label">Preparing your billing data...</p>
                    <div className="billing-skeleton-card">
                        <div className="billing-skeleton-line billing-skeleton-line-title"></div>
                        <div className="billing-skeleton-line billing-skeleton-line-value"></div>
                    </div>
                    <div className="billing-skeleton-card">
                        <div className="billing-skeleton-line billing-skeleton-line-title"></div>
                        <div className="billing-skeleton-line billing-skeleton-line-row"></div>
                        <div className="billing-skeleton-line billing-skeleton-line-row"></div>
                        <div className="billing-skeleton-line billing-skeleton-line-row"></div>
                    </div>
                </div>
            </div>
        );
    }

    const dailyFlowCost = summary?.daily_flow_cost || [];
    const totalCost = summary?.total_cost_usd || 0;
    const chartData = buildDailyChartData(dailyFlowCost);

    // Both views come from the same money; only the grouping differs.
    const displayGroups = (groupBy === 'project'
        ? toProjectGroups(summary?.workflow_groups)
        : toCategoryGroups(summary?.cost_groups)
    ).map((group) => ({
        ...group,
        summary: groupBy === 'project'
            ? `${group.count} charge${group.count === 1 ? '' : 's'}`
            : `${group.count} across ${group.rows.length} kind${group.rows.length === 1 ? '' : 's'}`,
    }));

    return (
        <div className="billing-container">
            <div className="billing-header">
                <h1>Billing</h1>
                <p className="billing-subtitle">What each piece of work actually cost</p>
            </div>

            {loading && hasLoadedOnce && (
                <div className="billing-loading-inline" aria-live="polite">
                    Updating billing data...
                </div>
            )}

            <div className="billing-range-controls">
                <label htmlFor="billing-range-select">Date Range</label>
                <select
                    id="billing-range-select"
                    value={selectedRange}
                    onChange={(e) => setSelectedRange(e.target.value)}
                >
                    <option value={RANGE_PRESETS.TODAY}>Today</option>
                    <option value={RANGE_PRESETS.WEEK}>This Week</option>
                    <option value={RANGE_PRESETS.MONTH}>This Month</option>
                    <option value={RANGE_PRESETS.YEAR}>This Year</option>
                    <option value={RANGE_PRESETS.CUSTOM}>Custom</option>
                </select>
            </div>

            {selectedRange === RANGE_PRESETS.CUSTOM && (
                <div className="billing-custom-range">
                    <div className="custom-range-field">
                        <label>Start Date</label>
                        <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                        />
                    </div>
                    <div className="custom-range-field">
                        <label>End Date</label>
                        <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                        />
                    </div>
                </div>
            )}

            {error && (
                <div className="error-message error">
                    <span className="error-icon"><TriangleAlert size={16} aria-hidden="true" /></span>
                    {error}
                    <button onClick={() => setError('')} className="error-close">×</button>
                </div>
            )}

            <div className="billing-summary-card">
                <h3>Total Spent So Far</h3>
                <div className="billing-total">{formatPrice(totalCost, 'USD')}</div>
            </div>

            <div className="billing-breakdown-card">
                <div className="billing-breakdown-head">
                    <h3>Where it went</h3>
                    <div className="billing-segmented" role="group" aria-label="Group spending by">
                        <button
                            type="button"
                            className={groupBy === 'category' ? 'is-active' : ''}
                            aria-pressed={groupBy === 'category'}
                            onClick={() => { setGroupBy('category'); setOpenGroups({}); }}
                        >
                            By category
                        </button>
                        <button
                            type="button"
                            className={groupBy === 'project' ? 'is-active' : ''}
                            aria-pressed={groupBy === 'project'}
                            onClick={() => { setGroupBy('project'); setOpenGroups({}); }}
                        >
                            By project
                        </button>
                    </div>
                </div>

                {displayGroups.length === 0 ? (
                    <div className="no-products">
                        <p>Nothing charged in this period.</p>
                    </div>
                ) : (
                    <ul className="billing-groups">
                        {displayGroups.map((group) => {
                            const isOpen = !!openGroups[group.id];
                            return (
                                <li key={group.id} className="billing-group">
                                    <button
                                        type="button"
                                        className="billing-group-head"
                                        aria-expanded={isOpen}
                                        onClick={() => setOpenGroups((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}
                                    >
                                        <span className="billing-group-swatch" style={{ background: group.color }} />
                                        <span className="billing-group-name">
                                            <strong>{group.label}</strong>
                                            <span className="billing-group-sub">{group.summary}</span>
                                        </span>
                                        <span className="billing-group-cost">
                                            <strong>{formatPrice(group.cost, 'USD')}</strong>
                                            <span className="billing-group-sub">{shareLabel(group.cost, totalCost)}</span>
                                        </span>
                                        <span className="billing-group-toggle">{isOpen ? 'Hide' : 'Details'}</span>
                                    </button>

                                    {isOpen && (
                                        <ul className="billing-group-rows">
                                            {group.rows.map((row, index) => (
                                                <li key={`${row.entity_flow}-${row.kind}-${index}`}>
                                                    <span>{itemLabel(row.entity_flow, row.kind, row.event_count)}</span>
                                                    <span className="billing-row-each">
                                                        {eachLabel(row.event_count, row.cost_usd, (v) => formatPrice(v, 'USD'))}
                                                    </span>
                                                    <span className="billing-row-cost">{formatPrice(row.cost_usd || 0, 'USD')}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}

                <p className="billing-footnote">
                    Costs are what the work actually used, measured after it ran.
                </p>
            </div>

            <div className="billing-breakdown-card">
                <h3>Daily Cost by Flow</h3>
                {dailyFlowCost.length === 0 ? (
                    <div className="no-products">
                        <p>No daily billing records found yet.</p>
                    </div>
                ) : (
                    <div className="billing-chart-wrap">
                        <Line
                            data={chartData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        position: 'top'
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: (context) => `${context.dataset.label}: ${formatPrice(context.parsed.y || 0, 'USD')}`
                                        }
                                    }
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        ticks: {
                                            callback: (value) => formatPrice(value, 'USD')
                                        }
                                    }
                                }
                            }}
                        />
                    </div>
                )}
            </div>

            <div className="billing-footer">
                <p className="footer-note">All cost values are rounded to 2 decimal places.</p>
            </div>
        </div>
    );
}

export { Billing };
